import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import {
  Plus, Trash2, Send, Save, Ban, ArrowLeft, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { RelatedDocsPanel } from '@/components/RelatedDocsPanel';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { DocstatusPill, StatusPill } from '@/components/StatusPill';
import { ApprovalWidget } from '@/components/ApprovalWidget';
import { Timeline } from '@/components/Timeline';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import { toast } from '@/components/Toaster';

// ---- Types mirror the Go structs in internal/accounting/paymententry. ----

type PaymentType = 'receive' | 'pay' | 'internal_transfer';
type PartyType   = 'customer' | 'supplier';
type RefDoctype  = 'sales_invoice' | 'purchase_invoice';

interface Account  { id: string; name: string; account_number?: string; account_currency: string; account_type?: string; root_type: string; }
interface Customer { id: string; name: string; display_name: string; }
interface Supplier { id: string; name: string; display_name: string; }
interface WhtType  { id: string; name: string; rate: string; account_id: string; }
interface SI       { id: string; name: string; customer_id: string; posting_date: string; grand_total: string; outstanding_amount: string; docstatus: 0 | 1 | 2; }
interface PI       { id: string; name: string; supplier_id: string; posting_date: string; grand_total: string; outstanding_amount: string; docstatus: 0 | 1 | 2; }

interface PaymentReference {
  id: string;
  row_index: number;
  reference_doctype: RefDoctype;
  reference_id: string;
  reference_name: string;
  total_amount: string;
  allocated_amount: string;
}
interface PaymentDeduction {
  id: string;
  row_index: number;
  account_id: string;
  description: string;
  amount: string;
  withholding_tax_type_id?: string;
}
interface PaymentEntry {
  id: string;
  name: string;
  payment_type: PaymentType;
  party_type?: PartyType;
  party_id?: string;
  posting_date: string;
  paid_from_account_id: string;
  paid_to_account_id: string;
  paid_from_currency: string;
  paid_to_currency: string;
  paid_amount: string;
  received_amount: string;
  source_exchange_rate: string;
  target_exchange_rate: string;
  total_allocated_amount: string;
  unallocated_amount: string;
  total_deductions: string;
  reference_no?: string;
  reference_date?: string;
  remarks?: string;
  docstatus: 0 | 1 | 2;
  references?: PaymentReference[];
  deductions?: PaymentDeduction[];
}

interface DraftRef {
  rowId: string;
  reference_id: string;
  allocated_amount: string;
}
interface DraftDed {
  rowId: string;
  account_id: string;
  description: string;
  amount: string;
  withholding_tax_type_id: string;
}

const rid = () => Math.random().toString(36).slice(2);
const todayISO = () => new Date().toISOString().slice(0, 10);

export function PaymentEntryForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['payment-entry', id],
    enabled: !isNew,
    queryFn: () => api<PaymentEntry>(`/accounting/payment-entries/${id}`),
  });

  // ---- Master-data lookups -------------------------------------------------
  const { data: accountsResp }  = useQuery({ queryKey: ['accounts'],  queryFn: () => api<{ items: Account[]  }>('/accounting/accounts') });
  const { data: customersResp } = useQuery({ queryKey: ['customers'], queryFn: () => api<{ items: Customer[] }>('/accounting/customers') });
  const { data: suppliersResp } = useQuery({ queryKey: ['suppliers'], queryFn: () => api<{ items: Supplier[] }>('/accounting/suppliers') });
  const { data: whtsResp }      = useQuery({ queryKey: ['withholdings'], queryFn: () => api<{ items: WhtType[] }>('/accounting/withholding-tax-types') });

  const accounts  = accountsResp?.items  ?? [];
  const customers = customersResp?.items ?? [];
  const suppliers = suppliersResp?.items ?? [];
  const whts      = whtsResp?.items      ?? [];

  // ---- Form state ----------------------------------------------------------
  const [paymentType, setPaymentType] = useState<PaymentType>('receive');
  const [partyID, setPartyID]         = useState<string>('');
  const [postingDate, setPostingDate] = useState<string>(todayISO());
  const [paidFromID, setPaidFromID]   = useState<string>('');
  const [paidToID, setPaidToID]       = useState<string>('');
  const [paidAmount, setPaidAmount]   = useState<string>('');
  const [refNo, setRefNo]             = useState<string>('');
  const [refDate, setRefDate]         = useState<string>('');
  const [remarks, setRemarks]         = useState<string>('');
  const [refs, setRefs]               = useState<DraftRef[]>([]);
  const [deds, setDeds]               = useState<DraftDed[]>([]);
  const [err, setErr]                 = useState<string | null>(null);

  // Hydrate from `existing` on edit.
  useEffect(() => {
    if (!existing) return;
    setPaymentType(existing.payment_type);
    setPartyID(existing.party_id ?? '');
    setPostingDate(existing.posting_date.slice(0, 10));
    setPaidFromID(existing.paid_from_account_id);
    setPaidToID(existing.paid_to_account_id);
    setPaidAmount(existing.paid_amount);
    setRefNo(existing.reference_no ?? '');
    setRefDate((existing.reference_date ?? '').slice(0, 10));
    setRemarks(existing.remarks ?? '');
    setRefs((existing.references ?? []).map((r) => ({
      rowId: r.id, reference_id: r.reference_id, allocated_amount: r.allocated_amount,
    })));
    setDeds((existing.deductions ?? []).map((d) => ({
      rowId: d.id, account_id: d.account_id, description: d.description,
      amount: d.amount, withholding_tax_type_id: d.withholding_tax_type_id ?? '',
    })));
  }, [existing]);

  // ---- Open invoices for the picked party ---------------------------------
  // For receive: only SIs for this customer with outstanding > 0.
  // For pay:    only PIs for this supplier with outstanding > 0.
  const invoiceEndpoint = paymentType === 'receive'
    ? '/accounting/sales-invoices'
    : paymentType === 'pay' ? '/accounting/purchase-invoices' : '';
  const refDoctype: RefDoctype = paymentType === 'receive' ? 'sales_invoice' : 'purchase_invoice';

  const { data: invoicesResp } = useQuery({
    queryKey: ['payment-invoices', invoiceEndpoint],
    queryFn:  () => api<{ items: (SI | PI)[] }>(invoiceEndpoint),
    enabled:  !!invoiceEndpoint,
  });
  const openInvoices = useMemo(() => {
    const all = invoicesResp?.items ?? [];
    return all.filter((inv) => {
      if (inv.docstatus !== 1) return false;
      if (new Decimal(inv.outstanding_amount || '0').lte(0)) return false;
      const pid = paymentType === 'receive' ? (inv as SI).customer_id : (inv as PI).supplier_id;
      return !partyID || pid === partyID;
    });
  }, [invoicesResp, partyID, paymentType]);

  // ---- Derived totals ------------------------------------------------------
  const totalAllocated = useMemo(
    () => refs.reduce((a, r) => a.plus(new Decimal(r.allocated_amount || '0')), new Decimal(0)),
    [refs],
  );
  const totalDeductions = useMemo(
    () => deds.reduce((a, d) => a.plus(new Decimal(d.amount || '0')), new Decimal(0)),
    [deds],
  );
  const paid = new Decimal(paidAmount || '0');
  const unallocated = paid.plus(totalDeductions).minus(totalAllocated);

  const editable  = isNew || existing?.docstatus === 0;
  const submitted = existing?.docstatus === 1;
  const cancelled = existing?.docstatus === 2;

  // ---- Options for the various pickers ------------------------------------
  const accountOptions = useMemo(
    () => accounts.map((a) => ({
      value: a.id,
      label: a.name,
      description: [a.account_number, a.account_currency, a.account_type].filter(Boolean).join(' · '),
    })),
    [accounts],
  );
  const partyOptions = useMemo(() => {
    if (paymentType === 'internal_transfer') return [];
    const list = paymentType === 'receive' ? customers : suppliers;
    return list.map((p) => ({ value: p.id, label: p.display_name, description: p.name }));
  }, [paymentType, customers, suppliers]);
  const invoiceOptions = useMemo(
    () => openInvoices.map((inv) => ({
      value: inv.id,
      label: inv.name,
      description: `${money(inv.outstanding_amount)} outstanding`,
    })),
    [openInvoices],
  );
  const whtOptions = useMemo(
    () => whts.map((w) => ({
      value: w.id, label: w.name, description: `${w.rate}%`,
    })),
    [whts],
  );

  // ---- Side-effects: keep dependent state coherent ------------------------
  // Switching payment type clears the party + invoice rows since they're
  // tied to a specific side.
  useEffect(() => {
    if (!isNew) return;
    setPartyID('');
    setRefs([]);
  }, [paymentType, isNew]);

  // ---- Validation ---------------------------------------------------------
  function validate(): string[] {
    const issues: string[] = [];
    if (!postingDate)  issues.push('Posting date is required.');
    if (!paidFromID)   issues.push('Paid from account is required.');
    if (!paidToID)     issues.push('Paid to account is required.');
    if (paidFromID && paidFromID === paidToID && paymentType !== 'internal_transfer') {
      issues.push('Paid from and paid to should be different accounts.');
    }
    if (!paidAmount || new Decimal(paidAmount).lte(0)) {
      issues.push('Paid amount must be greater than 0.');
    }
    if (paymentType !== 'internal_transfer' && !partyID) {
      issues.push(`Pick a ${paymentType === 'receive' ? 'customer' : 'supplier'}.`);
    }
    if (paymentType === 'internal_transfer' && (refs.length > 0 || deds.length > 0)) {
      issues.push('Internal transfer cannot have references or deductions.');
    }
    refs.forEach((r, i) => {
      if (!r.reference_id) issues.push(`Reference ${i + 1}: pick an invoice.`);
      if (!r.allocated_amount || new Decimal(r.allocated_amount).lte(0)) {
        issues.push(`Reference ${i + 1}: allocated amount must be > 0.`);
      }
    });
    deds.forEach((d, i) => {
      if (!d.account_id && !d.withholding_tax_type_id) {
        issues.push(`Deduction ${i + 1}: pick an account or a withholding type.`);
      }
      if (!d.amount || new Decimal(d.amount).lte(0)) {
        issues.push(`Deduction ${i + 1}: amount must be > 0.`);
      }
    });
    return issues;
  }

  // ---- Mutations ----------------------------------------------------------
  function buildBody(): Record<string, unknown> {
    const body: Record<string, unknown> = {
      payment_type: paymentType,
      posting_date: postingDate,
      paid_from_account_id: paidFromID,
      paid_to_account_id:   paidToID,
      paid_amount:          paidAmount,
      reference_no:         refNo || undefined,
      reference_date:       refDate || undefined,
      remarks:              remarks || undefined,
    };
    if (paymentType !== 'internal_transfer') {
      body.party_type = paymentType === 'receive' ? 'customer' : 'supplier';
      body.party_id   = partyID;
      body.references = refs
        .filter((r) => r.reference_id && r.allocated_amount)
        .map((r) => ({
          reference_doctype: refDoctype,
          reference_id:      r.reference_id,
          allocated_amount:  r.allocated_amount,
        }));
      body.deductions = deds
        .filter((d) => d.amount && (d.account_id || d.withholding_tax_type_id))
        .map((d) => ({
          account_id:              d.account_id || undefined,
          description:             d.description || undefined,
          amount:                  d.amount,
          withholding_tax_type_id: d.withholding_tax_type_id || undefined,
        }));
    }
    return body;
  }

  const createMutation = useMutation({
    mutationFn: () => api<PaymentEntry>('/accounting/payment-entries', { method: 'POST', body: buildBody() }),
    onSuccess: (pe) => {
      toast.success('Draft saved', pe.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/payment-entries'] });
      navigate({ to: `/accounting/payment-entries/${pe.id}` as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const updateMutation = useMutation({
    mutationFn: () => api<PaymentEntry>(`/accounting/payment-entries/${id}`, { method: 'PUT', body: buildBody() }),
    onSuccess: () => {
      toast.success('Saved', 'Draft updated.');
      qc.invalidateQueries({ queryKey: ['payment-entry', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/payment-entries'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const submitMutation = useMutation({
    mutationFn: () => api<PaymentEntry>(`/accounting/payment-entries/${id}/submit`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Submitted', 'Posted to GL; invoices settled.');
      qc.invalidateQueries({ queryKey: ['payment-entry', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/payment-entries'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Submit failed.'),
  });
  const cancelMutation = useMutation({
    mutationFn: () => api<PaymentEntry>(`/accounting/payment-entries/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.warning('Cancelled', 'Reversing GL entries posted.');
      qc.invalidateQueries({ queryKey: ['payment-entry', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/payment-entries'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Cancel failed.'),
  });

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }
  function onSave() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    updateMutation.mutate();
  }

  // ---- Row helpers --------------------------------------------------------
  function addRef() {
    setRefs((rs) => [...rs, { rowId: rid(), reference_id: '', allocated_amount: '' }]);
  }
  function patchRef(rowId: string, patch: Partial<DraftRef>) {
    setRefs((rs) => rs.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
  }
  function removeRef(rowId: string) {
    setRefs((rs) => rs.filter((r) => r.rowId !== rowId));
  }
  function pickRefInvoice(rowId: string, invID: string | null) {
    const inv = openInvoices.find((i) => i.id === invID);
    patchRef(rowId, {
      reference_id: invID ?? '',
      allocated_amount: inv ? new Decimal(inv.outstanding_amount).toString() : '',
    });
  }

  function addDed() {
    setDeds((ds) => [...ds, { rowId: rid(), account_id: '', description: '', amount: '', withholding_tax_type_id: '' }]);
  }
  function patchDed(rowId: string, patch: Partial<DraftDed>) {
    setDeds((ds) => ds.map((d) => (d.rowId === rowId ? { ...d, ...patch } : d)));
  }
  function removeDed(rowId: string) {
    setDeds((ds) => ds.filter((d) => d.rowId !== rowId));
  }
  function pickDedWht(rowId: string, whtID: string | null) {
    const w = whts.find((x) => x.id === whtID);
    patchDed(rowId, {
      withholding_tax_type_id: whtID ?? '',
      account_id: w ? w.account_id : '',
      description: w ? `Withholding ${w.rate}% (${w.name})` : '',
    });
  }

  const title = isNew ? 'New Payment Entry' : (existing?.name ?? '…');

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Finance', to: '/accounting' },
          { label: 'Payment Entries', to: '/accounting/payment-entries' },
          { label: isNew ? 'New' : (existing?.name ?? '…') },
        ]}
        title={title}
        status={existing && <DocstatusPill docstatus={existing.docstatus} />}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/accounting/payment-entries' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {editable && isNew && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft <Kbd>⌘S</Kbd>
              </Button>
            )}
            {editable && !isNew && (
              <Button variant="secondary" onClick={onSave} loading={updateMutation.isPending}>
                <Save className="size-4" /> Save
              </Button>
            )}
            {editable && !isNew && (
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>
                <Send className="size-4" /> Submit
              </Button>
            )}
            {submitted && (
              <Button variant="danger" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]"
      >
        <div className="space-y-4 min-w-0">
          {cancelled && (
            <Card className="!p-3 flex items-center gap-3 bg-danger/5 border-danger/30">
              <AlertCircle className="size-4 text-danger" />
              <span className="text-body text-danger">This Payment Entry was cancelled. Reversing GL entries are posted.</span>
            </Card>
          )}
          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          {!isNew && existing && (
            <ApprovalWidget doctype="payment_entry" documentId={existing.id} />
          )}

          {/* Header */}
          <Card>
            <CardTitle>Payment</CardTitle>
            <div className="mt-4 space-y-4">
              <Field label="Type">
                <div className="flex w-fit items-center gap-0.5 rounded-lg border border-hairline bg-surface p-0.5">
                  {(['receive', 'pay', 'internal_transfer'] as PaymentType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      disabled={!editable || !isNew}
                      onClick={() => setPaymentType(t)}
                      className={
                        'px-3 py-1 text-body-sm rounded-md transition-colors disabled:cursor-not-allowed ' +
                        (paymentType === t
                          ? 'bg-canvas text-ink font-medium shadow-sm'
                          : 'text-stone hover:text-ink')
                      }
                    >
                      {t === 'receive' ? 'Receive' : t === 'pay' ? 'Pay' : 'Internal transfer'}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                {paymentType !== 'internal_transfer' && (
                  <Field label={paymentType === 'receive' ? 'Customer' : 'Supplier'} hint="Required.">
                    <Combobox
                      options={partyOptions}
                      value={partyID || null}
                      onChange={(v) => { setPartyID(v ?? ''); setRefs([]); }}
                      placeholder={`Pick a ${paymentType === 'receive' ? 'customer' : 'supplier'}…`}
                      disabled={!editable}
                    />
                  </Field>
                )}
                <Field label="Posting date" hint={isNew ? undefined : 'Locked after creation.'}>
                  <Input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} disabled={!editable || !isNew} />
                </Field>
                <Field label="Reference no." hint="Cheque / transfer reference from the bank slip.">
                  <Input value={refNo} onChange={(e) => setRefNo(e.target.value)} disabled={!editable} />
                </Field>
                <Field label="Reference date">
                  <Input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} disabled={!editable} />
                </Field>
                <Field label="Remarks" hint="Free text — appears on the timeline.">
                  <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!editable} />
                </Field>
              </div>
            </div>
          </Card>

          {/* Accounts + amount */}
          <Card>
            <CardTitle>Cash flow</CardTitle>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field
                label="Paid from"
                hint={paymentType === 'receive'
                  ? 'AR account for the customer (debtors).'
                  : paymentType === 'pay'
                    ? 'Cash / bank account the money leaves.'
                    : 'Source bank/cash account.'}
              >
                <Combobox
                  options={accountOptions}
                  value={paidFromID || null}
                  onChange={(v) => setPaidFromID(v ?? '')}
                  placeholder="Pick account…"
                  disabled={!editable}
                />
              </Field>
              <Field
                label="Paid to"
                hint={paymentType === 'receive'
                  ? 'Cash / bank account that receives the money.'
                  : paymentType === 'pay'
                    ? 'AP account for the supplier (creditors).'
                    : 'Destination bank/cash account.'}
              >
                <Combobox
                  options={accountOptions}
                  value={paidToID || null}
                  onChange={(v) => setPaidToID(v ?? '')}
                  placeholder="Pick account…"
                  disabled={!editable}
                />
              </Field>
              <Field label="Paid amount" hint="In the paid-from account's currency.">
                <NumericInput value={paidAmount} onChange={setPaidAmount} currencyPrefix="Rp" decimalPlaces={0} placeholder="0" />
              </Field>
            </div>
          </Card>

          {/* References */}
          {paymentType !== 'internal_transfer' && (
            <Card padded={false}>
              <div className="flex items-center justify-between p-5 pb-3">
                <div>
                  <CardTitle>References ({refDoctype === 'sales_invoice' ? 'Sales Invoices' : 'Purchase Invoices'})</CardTitle>
                  <p className="text-caption text-stone mt-0.5">
                    Allocate this payment against open invoices for the picked party. Unreferenced money becomes an advance.
                  </p>
                </div>
                {editable && (
                  <Button variant="ghost" size="sm" onClick={addRef} disabled={!partyID}>
                    <Plus className="size-4" /> Add line
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead className="border-y border-hairline bg-surface-soft text-micro-uppercase text-stone">
                    <tr>
                      <th className="text-left font-medium px-3 py-2 w-12">#</th>
                      <th className="text-left font-medium px-3 py-2 min-w-[260px]">Invoice</th>
                      <th className="text-right font-medium px-3 py-2 w-40">Outstanding</th>
                      <th className="text-right font-medium px-3 py-2 w-44">Allocated</th>
                      {editable && <th className="w-10" />}
                    </tr>
                  </thead>
                  <tbody>
                    {refs.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-stone text-center">
                        {partyID ? 'No allocations yet. Add a line to settle outstanding invoices.' : 'Pick a party above first.'}
                      </td></tr>
                    )}
                    {refs.map((r, idx) => {
                      const inv = openInvoices.find((i) => i.id === r.reference_id)
                        ?? (existing?.references ?? []).find((er) => er.reference_id === r.reference_id);
                      const outstanding = inv && 'outstanding_amount' in inv ? (inv as SI | PI).outstanding_amount : (inv as PaymentReference | undefined)?.total_amount;
                      return (
                        <tr key={r.rowId} className="border-b border-hairline last:border-0">
                          <td className="px-3 py-2 text-stone">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            {editable
                              ? <Combobox
                                  options={invoiceOptions}
                                  value={r.reference_id || null}
                                  onChange={(v) => pickRefInvoice(r.rowId, v)}
                                  placeholder={partyID ? 'Pick invoice…' : 'Party required'}
                                  disabled={!partyID}
                                />
                              : <span className="px-3 text-charcoal">{
                                  existing?.references?.find((er) => er.reference_id === r.reference_id)?.reference_name ?? r.reference_id
                                }</span>}
                          </td>
                          <td className="px-3 py-2 text-right num text-stone">{outstanding ? money(String(outstanding)) : '—'}</td>
                          <td className="px-2 py-1.5">
                            {editable
                              ? <NumericInput
                                  value={r.allocated_amount}
                                  onChange={(v) => patchRef(r.rowId, { allocated_amount: v })}
                                  decimalPlaces={0}
                                />
                              : <span className="num text-right block px-3">{r.allocated_amount ? money(r.allocated_amount) : '—'}</span>}
                          </td>
                          {editable && (
                            <td className="px-2 py-1.5">
                              <Button variant="ghost" size="icon" aria-label="Remove row" onClick={() => removeRef(r.rowId)}>
                                <Trash2 className="size-3.5" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 sm:px-5 py-3 border-t border-hairline flex flex-wrap items-center justify-end gap-x-4 gap-y-2 num">
                <div className="text-caption text-stone">Allocated</div>
                <div className="font-medium">{money(totalAllocated.toString())}</div>
              </div>
            </Card>
          )}

          {/* Deductions */}
          {paymentType !== 'internal_transfer' && (
            <Card padded={false}>
              <div className="flex items-center justify-between p-5 pb-3">
                <div>
                  <CardTitle>Deductions (withholding, fees)</CardTitle>
                  <p className="text-caption text-stone mt-0.5">
                    Amounts that settle the invoice but didn't move as cash — e.g. PPh23 withheld, bank fees absorbed.
                  </p>
                </div>
                {editable && (
                  <Button variant="ghost" size="sm" onClick={addDed}>
                    <Plus className="size-4" /> Add line
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead className="border-y border-hairline bg-surface-soft text-micro-uppercase text-stone">
                    <tr>
                      <th className="text-left font-medium px-3 py-2 w-12">#</th>
                      <th className="text-left font-medium px-3 py-2 min-w-[200px]">Withholding type</th>
                      <th className="text-left font-medium px-3 py-2 min-w-[200px]">Account</th>
                      <th className="text-left font-medium px-3 py-2 min-w-[180px]">Description</th>
                      <th className="text-right font-medium px-3 py-2 w-40">Amount</th>
                      {editable && <th className="w-10" />}
                    </tr>
                  </thead>
                  <tbody>
                    {deds.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-6 text-stone text-center">
                        No deductions. Add one to record withholding tax or absorbed fees.
                      </td></tr>
                    )}
                    {deds.map((d, idx) => (
                      <tr key={d.rowId} className="border-b border-hairline last:border-0">
                        <td className="px-3 py-2 text-stone">{idx + 1}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox
                                options={whtOptions}
                                value={d.withholding_tax_type_id || null}
                                onChange={(v) => pickDedWht(d.rowId, v)}
                                placeholder="— Optional —"
                                clearable
                              />
                            : <span className="px-3 text-charcoal">{whts.find((w) => w.id === d.withholding_tax_type_id)?.name || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox
                                options={accountOptions}
                                value={d.account_id || null}
                                onChange={(v) => patchDed(d.rowId, { account_id: v ?? '' })}
                                placeholder="Pick account…"
                              />
                            : <span className="px-3 text-charcoal">{accounts.find((a) => a.id === d.account_id)?.name || d.account_id}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Input value={d.description} onChange={(e) => patchDed(d.rowId, { description: e.target.value })} />
                            : <span className="px-3 text-charcoal">{d.description || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={d.amount} onChange={(v) => patchDed(d.rowId, { amount: v })} decimalPlaces={0} />
                            : <span className="num text-right block px-3">{d.amount ? money(d.amount) : '—'}</span>}
                        </td>
                        {editable && (
                          <td className="px-2 py-1.5">
                            <Button variant="ghost" size="icon" aria-label="Remove row" onClick={() => removeDed(d.rowId)}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 sm:px-5 py-3 border-t border-hairline flex flex-wrap items-center justify-end gap-x-4 gap-y-2 num">
                <div className="text-caption text-stone">Deductions</div>
                <div className="font-medium">{money(totalDeductions.toString())}</div>
              </div>
            </Card>
          )}
        </div>

        {/* Right rail */}
        <aside className="space-y-4">
          <Card>
            <CardTitle>Totals</CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Paid"        value={money(paid.toString())} />
              <Row label="Allocated"   value={money(totalAllocated.toString())} />
              <Row label="Deductions"  value={money(totalDeductions.toString())} />
              <div className="border-t border-hairline pt-2 mt-2">
                <Row label="Unallocated" value={money(unallocated.toString())} />
              </div>
              {paid.isPositive() && (
                paid.plus(totalDeductions).equals(totalAllocated)
                  ? <StatusPill tone="success" withDot={false}><CheckCircle2 className="size-3" /> Fully allocated</StatusPill>
                  : unallocated.isNegative()
                    ? <StatusPill tone="danger" withDot={false}>Allocated &gt; paid + deductions</StatusPill>
                    : <StatusPill tone="neutral" withDot={false}>{money(unallocated.toString())} advance</StatusPill>
              )}
            </div>
          </Card>

          <Card>
            <CardTitle>Status</CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Status" value="" pill={existing ? <DocstatusPill docstatus={existing.docstatus} /> : <StatusPill tone="neutral">Draft</StatusPill>} />
              {existing && (
                <>
                  <Row label="Posting" value={existing.posting_date.slice(0, 10)} />
                  <Row label="From currency" value={existing.paid_from_currency} />
                  <Row label="To currency"   value={existing.paid_to_currency} />
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardTitle>How posting works</CardTitle>
            <ul className="mt-2 space-y-2 text-caption text-stone">
              <li><strong>Receive:</strong> Dr cash/bank + Dr deductions, Cr AR (customer).</li>
              <li><strong>Pay:</strong> Dr AP (supplier), Cr cash/bank + Cr deduction (e.g. PPh23 payable).</li>
              <li>Submit posts the GL <em>and</em> reduces the referenced invoices' outstanding atomically. Cancel reverses both.</li>
            </ul>
          </Card>

          {!isNew && existing && (
            <RelatedDocsPanel doctype="payment_entry" documentID={existing.id} />
          )}
          {!isNew && existing && (
            <Timeline hideViews doctype="payment_entry" documentId={existing.id} />
          )}
        </aside>
      </motion.div>
    </>
  );
}

function Row({ label, value, pill }: { label: string; value: string; pill?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-stone">{label}</span>
      {pill ?? <span className="text-ink num">{value}</span>}
    </div>
  );
}
